import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Building2, Users, Clock, Search, Loader2, Briefcase, Plus, Calendar, Filter, CheckCircle2, MapPin, ArrowRight, FileText } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import JobCard from '../components/dashboard/JobCard';
import JobDetailsModal from '../components/dashboard/JobDetailsModal';
import NewJobModal from '../components/dashboard/NewJobModal';
import JobFilters from '../components/candidates/JobFilters';
import FinalReportModal from '../components/dashboard/CompletedJobs/FinalReportModal';
import { jobsApi } from '../api/jobs';
import { candidatesApi } from '../api/candidates';
import { useLanguage } from '../contexts/LanguageContext';
import { useDataRefresh } from '../contexts/DataRefreshContext';

const Jobs = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { refreshCounters, refreshJobs } = useDataRefresh();
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showFinalReport, setShowFinalReport] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    department: '',
    priority: '',
    status: 'ACTIVE' as 'ACTIVE' | 'COMPLETED' | 'ALL', // Default to show only active jobs
    month: new Date().getMonth(),
    year: new Date().getFullYear()
  });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        console.log('Fetching jobs data, triggered by refreshCounter:', refreshCounters.jobs);
        const jobsData = await jobsApi.getAll();
        
        // Transform API data to match expected format
        const transformedJobs = await Promise.all(jobsData.map(async job => {
          // Calculate days until deadline
          const dueDate = new Date(job.application_deadline);
          const today = new Date();
          const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          // Determine priority based on days until deadline
          let priority;
          if (daysUntilDue < 0) {
            priority = 'overdue';
          } else if (daysUntilDue <= 3) {
            priority = 'high';
          } else {
            priority = 'low';
          }

          // Get candidates for this job
          const candidates = await jobsApi.getCandidates(job.id);
          
          // Get the actual total references provided (not just the completed ones)
          const totalReferencesProvided = candidates.reduce((total, candidate) => {
            return total + (candidate.references?.length || 0);
          }, 0);
          
          // Calculate references completed
          const referencesCompleted = candidates.reduce((total, candidate) => {
            return total + (candidate.references?.filter(ref => ref.filled_form_at)?.length || 0);
          }, 0);

          // Use the status from the job object or default to 'ACTIVE'
          const status = job.status || 'ACTIVE';

          return {
            id: job.id,
            title: job.job_title,
            department: job.department,
            location: 'Remote',
            candidates: candidates.length,
            referencesNeeded: totalReferencesProvided, // Use actual references provided instead of theoretical count
            referencesCompleted,
            status,
            priority,
            dueDate: job.application_deadline,
            daysUntilDue,
            completedDate: job.application_deadline // Using deadline as completion date for now
          };
        }));

        setJobs(transformedJobs);
        
        // Check if there's a job ID in the URL to automatically select
        const jobId = searchParams.get('jobId');
        if (jobId) {
          const jobToSelect = transformedJobs.find(job => job.id === jobId);
          if (jobToSelect) {
            setSelectedJob(jobToSelect);
          }
        }
        
        // Get all candidates for search cache update
        try {
          const allCandidates = await candidatesApi.getAll();
          
          // Collect all references from candidates
          const allReferences = allCandidates.reduce((refs, candidate) => {
            if (candidate.references && candidate.references.length > 0) {
              return [...refs, ...candidate.references];
            }
            return refs;
          }, [] as any[]);
          
          // Update search cache with the latest data
          import('../utils/search/searchItems').then(({ updateSearchCache }) => {
            updateSearchCache(jobsData, allCandidates, allReferences);
          });
        } catch (error) {
          console.error('Failed to update search cache:', error);
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
        setError(t('jobs.error.message'));
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [searchParams, refreshCounters.jobs]); // Add refreshCounters.jobs to dependencies

  const departments = Array.from(new Set(jobs.map(job => job.department))).sort();

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    // Keep the current status but reset all other filters
    setFilters(prev => ({
      ...prev,
      department: '',
      priority: '',
      month: -1,  // Show all months
      year: -1    // Show all years
    }));
  };

  // For month selection dropdown
  const months = [
    { value: -1, label: t('jobs.filters.allMonths') },
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' }
  ];

  // For year selection dropdown (current year and 2 previous years)
  const currentYear = new Date().getFullYear();
  const years = [
    { value: -1, label: t('jobs.filters.allYears') },
    { value: currentYear, label: currentYear.toString() },
    { value: currentYear - 1, label: (currentYear - 1).toString() },
    { value: currentYear - 2, label: (currentYear - 2).toString() }
  ];

  const filteredJobs = jobs
    .map(job => ({
      ...job,
      // Ensure every job has a title property
      title: job.title || job.job_title || '',
      // Ensure every job has an id property
      id: job.id || Math.random().toString(36).substring(2, 9)
    }))
    .filter(job => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        (job.title || '').toLowerCase().includes(searchLower) ||
        (job.department || '').toLowerCase().includes(searchLower);

      const matchesDepartment = !filters.department || (job.department || '') === filters.department;
      const matchesPriority = !filters.priority || (job.priority || '') === filters.priority;
      const matchesStatus = filters.status === 'ALL' || (job.status || 'ACTIVE') === filters.status;
      
      // Filter by date for completed jobs
      let matchesDate = true;
      if ((job.status === 'COMPLETED') && (filters.month !== undefined || filters.year !== undefined)) {
        try {
          const completedDate = job.completedDate || job.dueDate || new Date().toISOString();
          const jobDate = new Date(completedDate);
          
          if (filters.month !== undefined && filters.month !== -1 && !isNaN(jobDate.getTime())) {
            matchesDate = jobDate.getMonth() === filters.month;
          }
          
          if (filters.year !== undefined && filters.year !== -1 && matchesDate && !isNaN(jobDate.getTime())) {
            matchesDate = jobDate.getFullYear() === filters.year;
          }
        } catch (e) {
          console.error("Error parsing date:", e);
          matchesDate = true; // Default to showing the job if date parsing fails
        }
      }

      return matchesSearch && matchesDepartment && matchesPriority && matchesStatus && matchesDate;
    })
    // Sort by deadline (earliest first) for active jobs, completion date (most recent first) for completed jobs
    .sort((a, b) => {
      try {
        const statusA = a.status || 'ACTIVE';
        const statusB = b.status || 'ACTIVE';
        
        if (statusA === 'COMPLETED' && statusB === 'COMPLETED') {
          // Sort completed jobs by completion date (most recent first)
          const completedDateA = a.completedDate || a.dueDate || new Date().toISOString();
          const completedDateB = b.completedDate || b.dueDate || new Date().toISOString();
          
          const dateA = new Date(completedDateA).getTime();
          const dateB = new Date(completedDateB).getTime();
          
          return isNaN(dateA) || isNaN(dateB) ? 0 : dateB - dateA;
        } else if (statusA === 'ACTIVE' && statusB === 'ACTIVE') {
          // Sort active jobs by deadline (earliest first)
          const dueDateA = a.dueDate || new Date().toISOString();
          const dueDateB = b.dueDate || new Date().toISOString();
          
          const dateA = new Date(dueDateA).getTime();
          const dateB = new Date(dueDateB).getTime();
          
          return isNaN(dateA) || isNaN(dateB) ? 0 : dateA - dateB;
        } else {
          // Active jobs come before completed jobs
          return statusA === 'ACTIVE' ? -1 : 1;
        }
      } catch (err) {
        console.error("Error sorting jobs:", err);
        return 0;
      }
    });

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col">
        <Header />
        
        <main className="flex-1 p-4 lg:p-6">
          {/* Jobs Header Section */}
          <div className="bg-white rounded-xl shadow-sm border p-4 pb-3 lg:p-5 lg:pb-4 mb-1">
            <div className="flex items-center justify-between">
              <div className="mb-2">
                <h2 className="text-2xl font-bold text-gray-900 mb-1 flex items-center">
                  <Briefcase className="w-6 h-6 text-blue-600 mr-2" />
                  {t('jobs.title')}
                </h2>
                <p className="text-gray-500 text-sm tracking-wide">{t('jobs.subtitle')}</p>
              </div>
              <button
                onClick={() => setShowNewJobModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t('jobs.newJob')}</span>
              </button>
            </div>
            
            {/* Search bar */}
            <div className="mt-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('jobs.search.placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-blue-50/30 border-blue-200 hover:border-blue-300 transition-shadow"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 mt-1">
            <div className="bg-white rounded-xl shadow-sm border p-3 pt-3 lg:p-5 lg:pt-4 space-y-3">
              <div className="flex flex-col gap-3">
                {/* Status Filter - Buttons like in Candidates page */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.filters.jobStatus')}</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleFilterChange('status', 'ACTIVE')}
                      className={`px-3 py-1.5 rounded text-sm font-medium ${
                        filters.status === 'ACTIVE' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t('jobs.filters.ongoing')}
                    </button>
                    <button
                      onClick={() => handleFilterChange('status', 'COMPLETED')}
                      className={`px-3 py-1.5 rounded text-sm font-medium ${
                        filters.status === 'COMPLETED' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t('jobs.filters.completed')}
                    </button>
                    <button
                      onClick={() => handleFilterChange('status', 'ALL')}
                      className={`px-3 py-1.5 rounded text-sm font-medium ${
                        filters.status === 'ALL' 
                          ? 'bg-gray-800 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t('jobs.filters.allJobs')}
                    </button>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100 relative">
                  <div className="flex flex-wrap gap-4">
                    {/* Department filter */}
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.filters.department')}</label>
                      <div className="relative">
                        <select
                          value={filters.department}
                          onChange={(e) => handleFilterChange('department', e.target.value)}
                          className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-shadow text-gray-900 appearance-none bg-blue-50/30 border-blue-200 hover:border-blue-300"
                        >
                          <option value="">{t('jobs.filters.allDepartments')}</option>
                          {departments.map((department) => (
                            <option key={department} value={department}>
                              {department}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Priority filter - only show for ACTIVE jobs */}
                    {filters.status === 'ACTIVE' && (
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.filters.deadlineStatus')}</label>
                        <div className="relative">
                          <select
                            value={filters.priority}
                            onChange={(e) => handleFilterChange('priority', e.target.value)}
                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-shadow text-gray-900 appearance-none bg-blue-50/30 border-blue-200 hover:border-blue-300"
                          >
                            <option value="">{t('jobs.filters.allDeadlines')}</option>
                            <option value="overdue">{t('dashboard.activeJobs.priority.overdue')}</option>
                            <option value="high">{t('dashboard.activeJobs.priority.high')}</option>
                            <option value="low">{t('dashboard.activeJobs.priority.low')}</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Only show month and year filters when status is COMPLETED */}
                    {filters.status === 'COMPLETED' && (
                      <>
                        {/* Month filter */}
                        <div className="flex-1 min-w-[200px]">
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.filters.month')}</label>
                          <div className="relative">
                            <select
                              value={filters.month}
                              onChange={(e) => handleFilterChange('month', parseInt(e.target.value))}
                              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-shadow text-gray-900 appearance-none bg-blue-50/30 border-blue-200 hover:border-blue-300"
                            >
                              {months.map((month) => (
                                <option key={month.value} value={month.value}>
                                  {month.label}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            </div>
                          </div>
                        </div>

                        {/* Year filter */}
                        <div className="flex-1 min-w-[200px]">
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.filters.year')}</label>
                          <div className="relative">
                            <select
                              value={filters.year}
                              onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}
                              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-shadow text-gray-900 appearance-none bg-blue-50/30 border-blue-200 hover:border-blue-300"
                            >
                              {years.map((year) => (
                                <option key={year.value} value={year.value}>
                                  {year.label}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Clear filters button positioned in the top-right corner */}
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 px-2 py-1 rounded text-blue-600 hover:bg-blue-50 hover:text-blue-700 text-xs font-medium transition-colors absolute top-3 right-0"
                    title={t('common.clearFilters')}
                  >
                    <Filter className="w-2.5 h-2.5" />
                    <span>{t('common.clearFilters')}</span>
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3 text-gray-500">
                  <Loader2 className="w-10 h-10 animate-spin" />
                  <span>{t('jobs.loading')}</span>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-600 p-6 rounded-lg my-6">
                <h3 className="font-medium text-lg mb-2">{t('jobs.error.title')}</h3>
                <p>{error}</p>
              </div>
            ) : filteredJobs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredJobs.map((job) => {
                  // Ensure job has title property for backward compatibility
                  const jobWithTitle = {
                    ...job,
                    title: job.title || job.job_title
                  };
                  // For completed jobs, use the purple style similar to dashboard CompletedJobs
                  if (job.status === 'COMPLETED') {
                    return (
                      <div 
                        key={jobWithTitle.id} 
                        className="bg-white rounded-lg border border-purple-100 p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                        onClick={() => setSelectedJob({...jobWithTitle, status: 'COMPLETED'})}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="font-semibold text-gray-900">{jobWithTitle.title}</h3>
                          <div className="flex items-center gap-1 text-purple-600 bg-purple-50 px-2 py-1 rounded-full text-xs font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>{t('common.completed')}</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Building2 className="w-4 h-4" />
                            <span>{jobWithTitle.department}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{jobWithTitle.location}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Users className="w-4 h-4" />
                            <span>{jobWithTitle.candidates} {t('jobs.completedJob.candidates')}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>{t('jobs.completedJob.completedOn', { date: new Date(jobWithTitle.completedDate).toLocaleDateString() })}</span>
                          </div>
                        </div>
                        
                        <div className="pt-4 border-t border-purple-100 space-y-2">
                          <button
                            className="w-full flex items-center justify-center gap-1 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 py-2 rounded-lg transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedJob({...jobWithTitle, status: 'COMPLETED'});
                              setShowFinalReport(true);
                            }}
                          >
                            <FileText className="w-3.5 h-3.5 mr-1" /> {t('jobs.completedJob.seeFinalReport')}
                          </button>
                          
                          <button
                            className="w-full flex items-center justify-center gap-1 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 py-2 rounded-lg transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/candidates?jobId=${jobWithTitle.id}&status=COMPLETED`);
                            }}
                          >
                            {t('common.viewDetails')} <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  } else {
                    // For active jobs, use the standard JobCard component
                    return (
                      <JobCard 
                        key={jobWithTitle.id} 
                        job={jobWithTitle} 
                        onClick={setSelectedJob}
                        onJobCompleted={(completedJob) => {
                          // Update the job in the local state
                          setJobs(prevJobs => 
                            prevJobs.map(j => 
                              j.id === completedJob.id 
                                ? {...j, status: 'COMPLETED'} 
                                : j
                            )
                          );
                          
                          // Automatically switch to COMPLETED filter to show completed job
                          setFilters(prev => ({...prev, status: 'COMPLETED'}));
                        }}
                      />
                    );
                  }
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-lg border">
                <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">{t('jobs.empty.title')}</h3>
                <p className="text-gray-500 mt-2">
                  {searchQuery || Object.values(filters).some(v => v) 
                    ? t('jobs.empty.filtered')
                    : t('jobs.empty.subtitle')}
                </p>
              </div>
            )}
          </div>
        </main>
      </div>

      {selectedJob && (
        <JobDetailsModal 
          job={selectedJob} 
          onClose={() => setSelectedJob(null)} 
          onJobCompleted={(completedJob) => {
            // Update the job in the local state
            setJobs(prevJobs => 
              prevJobs.map(j => 
                j.id === completedJob.id 
                  ? {...j, status: 'COMPLETED'} 
                  : j
              )
            );
            // Update the selected job to show it's completed
            setSelectedJob({...selectedJob, status: 'COMPLETED'});
          }}
        />
      )}

      {showNewJobModal && (
        <NewJobModal 
          onClose={() => setShowNewJobModal(false)}
          onJobCreated={(newJob) => {
            try {
              console.log("Received new job:", newJob);
              // Transform the new job to match the structure expected by the UI
              const transformedJob = {
                ...newJob,
                id: newJob.id || Math.random().toString(36).substring(2, 9),
                title: newJob.job_title || '', // Add title property based on job_title
                job_title: newJob.job_title || '',
                department: newJob.department || '',
                status: 'ACTIVE',
                location: 'Remote',
                candidates: 0,
                referencesNeeded: newJob.required_number_of_references || 0,
                referencesCompleted: 0,
                priority: 'low',
                daysUntilDue: 30,
                dueDate: newJob.application_deadline || new Date().toISOString(),
                completedDate: newJob.application_deadline || new Date().toISOString(),
                // Ensure all properties have fallbacks
                description: newJob.description || ''
              };
              console.log("Transformed job:", transformedJob);
              setJobs(prev => [...prev, transformedJob]);
            } catch (err) {
              console.error("Error handling new job:", err);
            } finally {
              setShowNewJobModal(false);
            }
          }}
        />
      )}

      {showFinalReport && selectedJob && (
        <FinalReportModal
          jobId={selectedJob.id.toString()}
          jobTitle={selectedJob.title}
          onClose={() => setShowFinalReport(false)}
        />
      )}
    </div>
  );
};

export default Jobs;